import OrderProductList from '../components/OrderProductList';
import SellerRatingForm from '../components/SellerRatingForm';
import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import { payCustomerOrder, clearPendingCheckout } from '../services/checkoutService';
import { deliveryProviderLabel } from '../constants/deliveryProviders';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  useCancelCustomerOrder,
  useCustomerOrder,
} from '../hooks/useCustomerOrders';
import {
  customerOrderError,
  customerOrderStatusLabel,
  canCancelOrder,
  fulfillmentLabel,
  getOrderItems,
  orderDate,
  orderGrandTotal,
  refundLabel,
} from '../utils/orderPresentation';
import './customerOrders.css';
import PaymentChoice from '../components/PaymentChoice';

function OrderDetailsPage() {
  const { id } = useParams();
  const location = useLocation();
  const [message, setMessage] = useState('');
  const [canPay, setCanPay] = useState(false);
  const [paying, setPaying] = useState(false);
  const payActive = useRef(false);
  const [savedCardId, setSavedCardId] = useState('');
  useEffect(() => { let active = true; api.get('/api/payment/checkout-config').then(res => { if (active) setCanPay(res.data.allowed); }).catch(() => {}); return () => { active = false; }; }, []);
  const fallback = location.state?.order;
  const query = useCustomerOrder(id, fallback);
  useEffect(() => { if (query.data?.status === 'CANCELLED') clearPendingCheckout(id); }, [id, query.data?.status]);
  const cancel = useCancelCustomerOrder();
  const order = query.data;
  if (query.isLoading && !order)
    return (
      <div className="orders-page">
        <p>Загружаем заказ…</p>
      </div>
    );
  if (query.isError && !order)
    return (
      <div className="orders-page">
        <p className="order-error">{customerOrderError(query.error)}</p>
        <Link to="/orders">К моим заказам</Link>
      </div>
    );
  if (!order)
    return (
      <div className="orders-page">
        <p className="order-error">Заказ не найден.</p>
        <Link to="/orders">К моим заказам</Link>
      </div>
    );
  const items = getOrderItems(order);
  const refundStatus =
    order.refundIntent?.status || order.refund_intent?.status;
  const handleCancel = async () => {
    if (!window.confirm('Отменить заказ?')) return;
    setMessage('');
    try {
      await cancel.mutateAsync(id);
      clearPendingCheckout(id);
      setMessage('Заказ отменён.');
      await query.refetch();
    } catch (error) {
      setMessage(customerOrderError(error, 'отменить заказ'));
    }
  };
  const handlePay = async () => {
    if (payActive.current) return;
    payActive.current = true; setPaying(true); setMessage('');
    try { window.location.assign(await payCustomerOrder(id, Math.round(orderGrandTotal(order) * 100), savedCardId)); }
    catch (error) { setMessage(error.response?.data?.error || 'Не удалось открыть оплату. Проверьте её статус перед повтором.'); await query.refetch(); }
    finally { payActive.current = false; setPaying(false); }
  };
  return (
    <div className="orders-page order-detail-page">
      <Link to="/orders" className="back-link">
        ← Мои заказы
      </Link>
      <div className="orders-heading">
        <h1>Заказ</h1>
        <span className="status-badge">{customerOrderStatusLabel(order.status)}</span>
      </div>
      <p className="order-date">{orderDate(order)}</p>
      <div className="order-summary">
        <div>
          <span>Доставка</span>
          <b>{fulfillmentLabel(order.fulfillmentStatus)}</b>
        </div>
        <div>
          <span>Итого</span>
          <b>{orderGrandTotal(order)} ₽</b>
        </div>
      </div>
      {order.status === 'CREATED' && canPay && <><PaymentChoice value={savedCardId} onChange={setSavedCardId} disabled={paying || cancel.isPending} /><button className="order-primary-button" type="button" disabled={paying || cancel.isPending} onClick={handlePay}>{paying ? 'Открываем оплату…' : `Перейти к оплате ${orderGrandTotal(order)} ₽`}</button></>}
      <Link className="order-secondary-button" to={`/checkout/success?orderId=${id}`}>Проверить статус оплаты</Link>
      {canCancelOrder(order) && (
        <button
          className="order-danger-button"
          disabled={cancel.isPending || paying}
          onClick={handleCancel}
        >
          {cancel.isPending ? 'Отменяем…' : 'Отменить заказ'}
        </button>
      )}
      {message && (
        <p
          className={
            message.startsWith('Заказ отменён')
              ? 'order-success'
              : 'order-error'
          }
        >
          {message}
        </p>
      )}
      {refundStatus && refundLabel(refundStatus) && (
        <div className={`refund-note refund-${refundStatus.toLowerCase()}`}>
          <strong>{refundLabel(refundStatus)}</strong>
        </div>
      )}
      <section className="order-section">
        <h2>Товары</h2>
        {items.length ? (
          <OrderProductList items={items} />
        ) : (
          <p>Состав заказа пока недоступен.</p>
        )}
      </section>
      {(order.sellerOrders || order.seller_orders)?.length > 0 && (
        <section className="order-section">
          <h2>Продавцы и доставки</h2>
          {(order.sellerOrders || order.seller_orders).map((slice) => (
            <div className="seller-slice" key={slice.id || slice.sellerId}>
              <span>
                {slice.seller?.name ||
                  slice.sellerName ||
                  'Продавец'}
                <br />
                {slice.shipment && (
                  <small>
                    {deliveryProviderLabel(slice.shipment.provider)} ·{' '}
                    {shipmentLabel(slice.shipment.status)} ·{' '}
                    {slice.shipment.deliveryMethod === 'COURIER'
                      ? 'Курьер'
                      : 'ПВЗ'}
                  </small>
                )}
              </span>
              <b>{slice.amount || slice.totalAmount || 0} ₽</b>
              {order.status === 'PAID' && slice.status !== 'CANCELLED' && slice.fulfillmentStatus === 'completed' && <SellerRatingForm slice={slice} />}
            </div>
          ))}
        </section>
      )}
      <section className="order-section">
        <h2>Доставка</h2>{order.shipments?.some(s => s.consolidated) && <p className="buyer-notice">Выбранные товары отправляются одной общей посылкой со склада MaxMarket.</p>}{order.shipments?.some(s => s.itemOutcomes?.requiresReview) && <p role="status" className="buyer-notice">Есть данные об отказе или расхождении состава. Оператор проверит позиции и возврат оплаты.</p>}
        <p>{order.address || 'Адрес пункта выдачи уточняется.'}</p>
        {order.deliveryCost != null && (
          <p>Стоимость доставки: {order.deliveryCost} ₽</p>
        )}
      </section>
    </div>
  );
}
function shipmentLabel(status) {
  return (
    {
      DRAFT: 'Готовится',
      PROCESSING: 'Проверяется',
      READY_FOR_HANDOVER: 'Готов к передаче',
      IN_TRANSIT: 'Передан в доставку',
      OUT_FOR_DELIVERY: 'Доставляется',
      DELIVERED: 'Доставлен',
      DELIVERY_FAILED: 'Проблема с доставкой',
      CANCELLED: 'Отменён',
      RETURNING: 'Возвращается',
      RETURNED: 'Возвращён',
      UNKNOWN: 'Статус уточняется',
    }[status] || 'Статус уточняется'
  );
}
export default OrderDetailsPage;
